import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  externalPlanSelectionService,
  ExternalPlanSelectionRequestError,
} from '@/services/provider/external-plan-selection.service';
import type {
  ExternalPlanSelectionConfig,
  ExternalPlanSelectionState,
  RotateExternalPlanSelectionResult,
  UpdateExternalPlanSelectionInput,
} from '@/types/external-plan-selection';

const EMPTY_CONFIG: ExternalPlanSelectionConfig = {
  partnerName: '',
  enabled: false,
  allowedDomains: [],
  secretPreview: null,
  lastRotatedAt: null,
};

export const externalPlanSelectionKeys = {
  all: ['provider', 'external-plan-selection'] as const,
  detail: () => [...externalPlanSelectionKeys.all, 'detail'] as const,
};

export function useExternalPlanSelectionSettings(enabled = true) {
  return useQuery<ExternalPlanSelectionState, ExternalPlanSelectionRequestError>({
    queryKey: externalPlanSelectionKeys.detail(),
    queryFn: () => externalPlanSelectionService.getSettings(),
    enabled,
  });
}

export function useUpdateExternalPlanSelectionSettings() {
  const queryClient = useQueryClient();

  return useMutation<ExternalPlanSelectionConfig, ExternalPlanSelectionRequestError, UpdateExternalPlanSelectionInput>({
    mutationFn: (input) => externalPlanSelectionService.updateSettings(input),
    onSuccess: (config) => {
      queryClient.setQueryData(externalPlanSelectionKeys.detail(), (previous?: ExternalPlanSelectionState) => ({
        config,
        metrics: previous?.metrics ?? null,
      }));
    },
  });
}

export function useRotateExternalPlanSelectionSecret() {
  const queryClient = useQueryClient();

  return useMutation<RotateExternalPlanSelectionResult, ExternalPlanSelectionRequestError>({
    mutationFn: () => externalPlanSelectionService.rotateSecret(),
    onSuccess: (result) => {
      queryClient.setQueryData(externalPlanSelectionKeys.detail(), (previous?: ExternalPlanSelectionState) => ({
        config: result.config ?? previous?.config ?? EMPTY_CONFIG,
        metrics: previous?.metrics ?? null,
      }));
    },
  });
}
